import {announce} from '@github-ui/aria-live'
import {useState} from 'react'
import {Button, Flash, IconButton} from '@primer/react'
import {CircleSlashIcon} from '@primer/octicons-react'
import {useEnableAutoMergeMutation} from '../../hooks/mutations/use-enable-auto-merge-mutation'
import {useMergeMethodContext} from '../../contexts/MergeMethodContext'
import {useSidebarMergeData} from './use-sidebar-merge-data'
import {mergeButtonText} from '../../helpers/merge-button-text'
import styles from './SidebarMergeWhenReadyButton.module.css'

/**
 * Renders the direct-merge auto-merge button for repositories without a merge queue when the
 * pull request isn't mergeable yet but the viewer can enable auto-merge. This mirrors the docked
 * merge panel's auto-merge action: a neutral, active button labeled "Enable auto-merge" that
 * merges the pull request automatically once its remaining requirements (checks, approvals) are met.
 */
export function SidebarEnableAutoMergeButton() {
  const {mergeBoxPullRequest} = useSidebarMergeData()
  const {mergeMethod} = useMergeMethodContext()
  const baseRefName = mergeBoxPullRequest?.baseRefName ?? 'main'

  const [confirming, setConfirming] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const {mutate: enableAutoMerge, isPending} = useEnableAutoMergeMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      announce(e.message)
    },
    onSuccess: () => {
      setConfirming(false)
      setErrorMessage(undefined)
    },
  })

  return (
    <div className="d-flex flex-column gap-2 pt-2">
      {errorMessage && <Flash variant="danger">{errorMessage}</Flash>}
      <div className={styles.buttonRow}>
        <div className={styles.primaryButton}>
          <Button
            block
            loading={isPending}
            loadingAnnouncement="Enabling auto-merge"
            onClick={() => {
              if (confirming) {
                setErrorMessage(undefined)
                enableAutoMerge({mergeMethod})
              } else {
                setConfirming(true)
              }
            }}
          >
            {isPending ? '' : mergeButtonText({mergeMethod, confirming, isAutoMergeAllowed: true})}
          </Button>
        </div>
        <div className={`${styles.cancelButton} ${confirming ? styles.cancelButtonVisible : ''}`}>
          <IconButton
            icon={CircleSlashIcon}
            aria-label="Cancel"
            disabled={isPending}
            onClick={() => {
              setConfirming(false)
              setErrorMessage(undefined)
            }}
            tabIndex={confirming ? 0 : -1}
          />
        </div>
      </div>
      <span className="text-small color-fg-muted">
        This pull request will merge automatically into <span className="text-semibold">{baseRefName}</span> once all
        requirements are met.
      </span>
    </div>
  )
}
